import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';
import { BE_DEFINES } from 'constants/backend-defines.const';
import {
  BOOTH_STORAGE_KEY,
  IS_MULTIPLE_CAMERA_STORAGE_KEY,
} from 'constants/common.const';
import {
  PRINTING_PHOTO_LIFE_CYCLE_STEP_DEFAULT,
  SELFE_LIFE_CYCLE_STEP_DEFAULT,
} from 'constants/photo.const';
import { includesId } from 'helpers/array.helper';
import { setSessionStorage } from 'helpers/common.helper';
import { isEqualVal } from 'helpers/string.helper';
import { formatDate } from 'helpers/time.helper';
import { BoothStateModel } from 'models/photo/booth.model';
import moment from 'moment';
import persistReducer from 'redux-persist/es/persistReducer';
import storage from 'redux-persist/lib/storage';
import { getBoothApiAction } from 'store/api/booth.slice-api';
import { getNumberOfRemainApiAction } from 'store/api/print.slice-api';

const initialState: BoothStateModel = {
  boothId: '',
  boothInfo: null,

  isNormal: true,
  isOverhead: false,
  isRobotArm: false,
  isVerticalScreen: false,
  isRotatablePhoto: true,
  isTurnOnSticker: false,
  isTurnOnBackground: false,
  isTurnOnPayment: true,
  isTurnOnMomoMethodPayment: false,
  isTurnOnVNPayMethodPayment: false,
  isTakePhotoOrigin: false,
  isMultipleCamera: false,
  isThemeBeforePayment: false,
  isUseSound: false,
  isOnFeedback: false,
  isEventBooth: false,
  isShowQrTransactionImage: false,

  TestedBooth: null,
  LifeCycleStep: SELFE_LIFE_CYCLE_STEP_DEFAULT,
  PrintingLifeCycleStep: PRINTING_PHOTO_LIFE_CYCLE_STEP_DEFAULT,

  loadingInList: false,
  loadingInitial: true,
  numberOfRemain: 0,
};

export const boothSlice = createSlice({
  name: 'booth',
  initialState,
  reducers: {
    setBoothStateAction: (
      state,
      action: PayloadAction<Partial<BoothStateModel>>,
    ) => ({ ...state, ...action?.payload }),
    finishTestedBooth: (state) => {
      const isDifferentDate =
        formatDate(state.TestedBooth?.date) !== formatDate(moment());

      return {
        ...state,
        TestedBooth: {
          ...state.TestedBooth,
          date: isDifferentDate
            ? formatDate(moment())
            : state.TestedBooth?.date,
          numberOfUses: isDifferentDate
            ? 1
            : (state.TestedBooth?.numberOfUses || 0) + 1,
        },
      } as BoothStateModel;
    },
  },
  extraReducers: (builder) => {
    builder
      .addMatcher(getBoothApiAction.matchPending, (state) => ({
        ...state,
        loadingInList: true,
      }))
      .addMatcher(getBoothApiAction.matchRejected, (state) => ({
        ...state,
        ...initialState,
        loadingInitial: false,
      }))
      .addMatcher(getBoothApiAction.matchFulfilled, (state, { payload }) => {
        // lưu booth config
        setSessionStorage(
          IS_MULTIPLE_CAMERA_STORAGE_KEY,
          payload?.IsMultipleCamera ? '1' : '',
        );

        return {
          ...state,
          boothId: payload?.boothId || '',
          boothInfo: payload,
          loadingInList: false,
          isNormal: !includesId(
            [BE_DEFINES.OVERHEAD_BOOTH, BE_DEFINES.OVERHEAD_BOOTH],
            payload?.Type,
          ),
          isVerticalScreen: isEqualVal(
            payload?.ScreenType,
            BE_DEFINES.SCREEN_TYPE.VERTICAL,
          ),
          isRobotArm: isEqualVal(payload?.Type, BE_DEFINES.ROBOT_ARM_BOOTH),
          isOverhead: isEqualVal(payload?.Type, BE_DEFINES.OVERHEAD_BOOTH),

          isRotatablePhoto: true, // !!payload?.IsRotatePhoto,
          isTurnOnSticker: !!payload?.IsSticker,
          isTurnOnBackground: !!payload?.IsDigitalBackground,
          isTurnOnPayment: !!payload?.IsPay, // !!payload?.IsPay,
          isTurnOnMomoMethodPayment: !!payload?.IsUsePayMomoQR,
          isTurnOnVNPayMethodPayment: !!payload?.IsUseVnPayQR,
          isTakePhotoOrigin: !!payload?.IsTakePhotoOrigin,
          isMultipleCamera: !!payload?.IsMultipleCamera,
          isThemeBeforePayment: !!payload?.IsSelectThemeBeforePay,
          isUseSound: !!payload?.IsSoundEffect,
          isOnFeedback: !!payload?.IsFeedback,
          isEventBooth: isEqualVal(payload?.Type, BE_DEFINES.EVENT_BOOTH),
          isShowQrTransactionImage: !!payload?.IsShowQRDownload,
        };
      })
      .addMatcher(getNumberOfRemainApiAction.matchPending, (state) => ({
        ...state,
        loadingInList: true,
      }))
      .addMatcher(getNumberOfRemainApiAction.matchRejected, (state) => ({
        ...state,
        loadingInList: false,
      }))
      .addMatcher(
        getNumberOfRemainApiAction.matchFulfilled,
        (state, { payload }) => ({
          ...state,
          loadingInList: false,
          numberOfRemain: payload?.response?.NumberOfPrint || 0,
        }),
      );
  },
});

export const { setBoothStateAction, finishTestedBooth } = boothSlice.actions;

const persistConfig = {
  key: BOOTH_STORAGE_KEY,
  whitelist: ['TestedBooth'],
  storage,
};

export default persistReducer(persistConfig, boothSlice.reducer);
